import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { pricingConfig } from '../data/pricing';
import type { BundleId } from '../data/pricing';
import type { CartLine } from '../types/cart';
import { priceIndividual, priceLine, priceSystem } from '../utils/pricing';
import { track } from '../utils/analytics';
type CartContextValue = {
  lines: CartLine[];
  addIndividual: (qty: number) => void;
  addSystem: (bundleId: BundleId, extras: number) => void;
  setIndividualQty: (id: string, qty: number) => void;
  setExtras: (id: string, extras: number) => void;
  remove: (id: string) => void;
  clear: () => void;
  totals: {
    lights: number;
    total: number;
    savings: number;
  };
};
const CartContext = createContext<CartContextValue | null>(null);
const STORAGE_KEY = 'mfl-cart-v1';
const uid = () => Math.random().toString(36).slice(2, 10);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) as CartLine[] : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  }, [lines]);
  const addIndividual = (qty: number) => {
    track('add_to_cart', {
      kind: 'individual',
      content_ids: ['MFL-M1PRO-1'],
      content_name: 'M1 PRO — Build Your Own',
      content_type: 'product',
      contents: [{
        id: 'MFL-M1PRO-1',
        quantity: qty,
        item_price: pricingConfig.unitPrice
      }],
      num_items: qty,
      value: priceIndividual(qty).total,
      currency: pricingConfig.currency
    });
    setLines(prev => {
      const existing = prev.find(l => l.kind === 'individual');
      if (existing && existing.kind === 'individual') {
        return prev.map(l => l.id === existing.id && l.kind === 'individual' ? {
          ...l,
          qty: Math.min(l.qty + qty, pricingConfig.maxIndividualQty)
        } : l);
      }
      return [...prev, {
        id: uid(),
        kind: 'individual',
        qty
      }];
    });
  };
  const addSystem = (bundleId: BundleId, extras: number) => {
    const sys = priceSystem(bundleId, extras);
    track('add_to_cart', {
      kind: 'system',
      bundleId,
      content_ids: [sys.bundle.sku],
      content_name: sys.bundle.name,
      content_type: 'product',
      contents: [{
        id: sys.bundle.sku,
        quantity: 1,
        item_price: sys.total
      }],
      num_items: sys.lights,
      extras,
      value: sys.total,
      currency: pricingConfig.currency
    });
    setLines(prev => [...prev, {
      id: uid(),
      kind: 'system',
      bundleId,
      extras
    }]);
  };
  const setIndividualQty = (id: string, qty: number) => setLines(prev => prev.map(l => l.id === id && l.kind === 'individual' ? {
    ...l,
    qty
  } : l));
  const setExtras = (id: string, extras: number) => setLines(prev => prev.map(l => l.id === id && l.kind === 'system' ? {
    ...l,
    extras
  } : l));
  const remove = (id: string) => setLines(prev => prev.filter(l => l.id !== id));
  const clear = () => setLines([]);
  const totals = useMemo(() => lines.reduce((acc, l) => {
    const p = priceLine(l);
    return {
      lights: acc.lights + p.lights,
      total: Math.round((acc.total + p.total) * 100) / 100,
      savings: Math.round((acc.savings + p.savings) * 100) / 100
    };
  }, {
    lights: 0,
    total: 0,
    savings: 0
  }), [lines]);
  return <CartContext.Provider value={{
    lines,
    addIndividual,
    addSystem,
    setIndividualQty,
    setExtras,
    remove,
    clear,
    totals
  }} data-id="element-975" data-mp="20g5vfy7dlzcp">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}